import type { Components } from '@/api/generated/api';
import type { TTranslate } from '@/interfaces';
import { getYearOfDateYY, getYearOfDateYYYY } from '@/utils/date.utils';
import { WeekRepresentation } from 'common/enum/user.setting.enum';
import { differenceInCalendarISOWeeks, getISOWeek, startOfISOWeek } from 'date-fns';

type SchoolYearDates = Pick<Components.Schemas.SchoolYearDto, 'schoolYearStart' | 'schoolYearEnd'>;

export interface IWeekNumberSettings {
  weekRepresentation: WeekRepresentation;
  schoolYear: SchoolYearDates | null;
}

export interface IWeekNumber {
  representation: WeekRepresentation;
  week: number;
}

/** The school year's year range, e.g. "2024/25", derived from its start/end dates. */
export const formatSchoolYearRange = (schoolYear: SchoolYearDates): string =>
  `${getYearOfDateYYYY(schoolYear.schoolYearStart)}/${getYearOfDateYY(schoolYear.schoolYearEnd)}`;

/**
 * The localized school-year display name, e.g. "Schuljahr 2024/25". The name is NOT stored on the model — it is
 * derived from the dates here so every site shows the same i18n-correct label.
 */
export const getSchoolYearName = (schoolYear: SchoolYearDates, t: TTranslate): string =>
  t('schoolYears.schoolYearName', { year: formatSchoolYearRange(schoolYear) });

/**
 * The school week of a day: SW1 is the Monday–Sunday week containing the first school day, and every later calendar
 * week counts, holidays included. Null for weeks outside the school year.
 */
export const getSchoolWeekNumber = (day: Date, schoolYear: SchoolYearDates): number | null => {
  // Bounds are read in local time like the timeline week keys: a start stored as local midnight by a server east of
  // UTC has the previous day as its UTC date part.
  const firstWeekStart = startOfISOWeek(new Date(schoolYear.schoolYearStart));
  const lastWeekStart = startOfISOWeek(new Date(schoolYear.schoolYearEnd));
  const weekStart = startOfISOWeek(day);
  if (weekStart < firstWeekStart || weekStart > lastWeekStart) return null;
  return differenceInCalendarISOWeeks(weekStart, firstWeekStart) + 1;
};

/** The week number to show for a day: the school week when selected and known, otherwise the ISO calendar week. */
export const resolveWeekNumber = (day: Date, settings: IWeekNumberSettings): IWeekNumber => {
  const schoolWeek =
    settings.weekRepresentation === WeekRepresentation.schoolWeek && settings.schoolYear
      ? getSchoolWeekNumber(day, settings.schoolYear)
      : null;
  if (schoolWeek !== null) return { representation: WeekRepresentation.schoolWeek, week: schoolWeek };
  return { representation: WeekRepresentation.calendarWeek, week: getISOWeek(day) };
};
